import React from 'react'
import { useTranslation } from 'react-i18next'
import { Vehicle, VehicleStatus } from '../types'
import { MapPin, User, Battery, Signal, Clock } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'

interface VehicleListProps {
  vehicles: Vehicle[]
  selectedVehicle: Vehicle | null
  onVehicleSelect: (vehicle: Vehicle) => void
  loading: boolean
}

const VehicleList: React.FC<VehicleListProps> = ({
  vehicles,
  selectedVehicle,
  onVehicleSelect,
  loading
}) => {
  const { t } = useTranslation()
  const getStatusColor = (status: VehicleStatus) => {
    switch (status) {
      case VehicleStatus.MOVING:
        return 'bg-green-500'
      case VehicleStatus.STOPPED:
        return 'bg-red-500'
      case VehicleStatus.IDLE:
        return 'bg-yellow-500'
      case VehicleStatus.OFFLINE:
        return 'bg-gray-500'
      case VehicleStatus.MAINTENANCE:
        return 'bg-orange-500'
      default:
        return 'bg-gray-500'
    }
  }

  const getStatusText = (status: VehicleStatus) => {
    switch (status) {
      case VehicleStatus.MOVING:
        return 'En movimiento'
      case VehicleStatus.STOPPED:
        return 'Detenido'
      case VehicleStatus.IDLE:
        return 'Inactivo'
      case VehicleStatus.OFFLINE:
        return 'Sin conexión'
      case VehicleStatus.MAINTENANCE:
        return 'Mantenimiento'
      default:
        return 'Desconocido'
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="px-4 pt-3 pb-2 border-b border-gray-100">
        <input
          type="search"
          placeholder={t('search')}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
          aria-label={t('search')}
        />
      </div>
      <div className="p-4 space-y-2">
        {vehicles.map((vehicle) => (
          <div
            key={vehicle.id}
            onClick={() => onVehicleSelect(vehicle)}
            className={`p-3 rounded-lg border cursor-pointer transition-all hover:shadow-md ${
              selectedVehicle?.id === vehicle.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            {/* Vehicle Header */}
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <div className={`w-3 h-3 rounded-full ${getStatusColor(vehicle.status)}`}></div>
                <h3 className="font-medium text-gray-800">{vehicle.name}</h3>
              </div>
              <span className="text-xs text-gray-500">{vehicle.plateNumber}</span>
            </div>

            {/* Status and Speed */}
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">{getStatusText(vehicle.status)}</span>
              <span className="text-sm font-medium text-gray-800">{vehicle.speed} km/h</span>
            </div>

            {/* Driver Info */}
            {vehicle.driver && (
              <div className="flex items-center space-x-2 mb-2">
                <User className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600">{vehicle.driver.name}</span>
              </div>
            )}

            {/* Location */}
            <div className="flex items-center space-x-2 mb-2">
              <MapPin className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600 truncate">
                {vehicle.location.address || `${vehicle.location.latitude.toFixed(4)}, ${vehicle.location.longitude.toFixed(4)}`}
              </span>
            </div>

            {/* Device Status */}
            <div className="flex items-center justify-between text-xs text-gray-500">
              <div className="flex items-center space-x-3">
                {vehicle.batteryLevel && (
                  <div className="flex items-center space-x-1">
                    <Battery className="w-3 h-3" />
                    <span>{vehicle.batteryLevel}%</span>
                  </div>
                )}
                {vehicle.signalStrength && (
                  <div className="flex items-center space-x-1">
                    <Signal className="w-3 h-3" />
                    <span>{vehicle.signalStrength}%</span>
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-1">
                <Clock className="w-3 h-3" />
                <span>
                  {formatDistanceToNow(vehicle.lastUpdate, { 
                    addSuffix: true, 
                    locale: es 
                  })}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {vehicles.length === 0 && (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500">
          <MapPin className="w-12 h-12 mb-2" />
          <p>{t('no_vehicles')}</p>
        </div>
      )}
    </div>
  )
}

export default VehicleList
